import styles from "./Button.module.scss";

import { BaseButton, ButtonVariant } from "./Base";
import {
  Spinner,
  SpinnerAppearance,
  SpinnerSize,
  SpinnerVariant,
} from "../Spinner";

import { cn } from "@/utils/cn";

enum ButtonAlign {
  Left = "left",
  Center = "center",
}

enum ButtonShape {
  Square = "square",
  Rounded = "rounded",
  Circle = "circle",
  Pill = "pill",
}

enum ButtonSize {
  ExtraSmall = "extraSmall",
  Small = "small",
  Medium = "medium",
  Large = "large",
}

interface ButtonProps extends React.ComponentProps<typeof BaseButton> {
  prefix?: React.ReactNode;
  suffix?: React.ReactNode;
  shouldResizeIcons?: boolean;
  isLoading?: boolean;
  isIconOnly?: boolean;
  align?: ButtonAlign;
  shape?: ButtonShape;
  size?: ButtonSize;
}

const Button: React.FC<ButtonProps> = ({
  prefix,
  suffix,
  isLoading,
  className,
  shouldResizeIcons = true,
  isIconOnly,
  children,
  disabled,
  variant,
  size = ButtonSize.Medium,
  shape = ButtonShape.Rounded,
  align = ButtonAlign.Center,
  ...rest
}) => {
  return (
    <BaseButton
      data-align={align}
      disabled={isLoading || disabled}
      data-auto-resize-icons={shouldResizeIcons}
      data-icon-only={isIconOnly}
      variant={variant}
      className={cn(
        styles.button,
        styles[align],
        styles[size],
        styles[shape],
        className
      )}
      {...rest}
    >
      {isLoading ? (
        <Spinner
          size={SpinnerSize.Small}
          variant={variant as unknown as SpinnerVariant}
          appearance={SpinnerAppearance.Solid}
        />
      ) : (
        <>
          {prefix && <span className={styles.prefix}>{prefix}</span>}
          {children}
          {suffix && <span className={styles.suffix}>{suffix}</span>}
        </>
      )}
    </BaseButton>
  );
};

export { Button, ButtonAlign, ButtonShape, ButtonSize };
export type { ButtonProps };
